import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Mail, Lock, KeyRound, ArrowLeft } from "lucide-react";
import { z } from "zod";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

type Step = "email" | "otp" | "newPassword";

const ForgotPasswordModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const emailSchema = z.string().trim().email(t('auth.validation.emailInvalid')).max(255);
  const passwordSchema = z.object({
    newPassword: z.string().min(6, t('auth.validation.passwordMin')).max(100),
    confirmPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('auth.validation.passwordMismatch'),
    path: ["confirmPassword"],
  });

  const resetState = () => {
    setStep("email"); setEmail(""); setOtp(""); setNewPassword(""); setConfirmPassword(""); setErrors({}); setLoading(false);
  };

  const handleOpenChange = (v: boolean) => { setOpen(v); if (!v) resetState(); };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const normalizedEmail = email.trim().toLowerCase();

    try {
      emailSchema.parse(normalizedEmail);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors({ email: err.errors[0].message });
        return;
      }
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("password-reset-otp", {
        body: { action: "send", email: normalizedEmail },
      });

      if (error) throw error;
      setEmail(normalizedEmail);
      toast({ title: t('forgotPassword.codeSent'), description: t('forgotPassword.codeSentDesc') });
      setStep("otp");
    } catch (err: any) {
      toast({ title: t('auth.error'), description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setErrors({ otp: t('forgotPassword.otpError') });
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("password-reset-otp", {
        body: {
          action: "verify",
          email: email.trim().toLowerCase(),
          otp,
        },
      });

      if (error) throw error;
      toast({ title: t('forgotPassword.codeVerified'), description: t('forgotPassword.codeVerifiedDesc') });
      setStep("newPassword");
    } catch (err: any) {
      toast({ title: t('forgotPassword.invalidCode'), description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try { passwordSchema.parse({ newPassword, confirmPassword }); } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((e) => { newErrors[String(e.path[0])] = e.message; });
        setErrors(newErrors); return;
      }
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("password-reset-otp", {
        body: {
          action: "verify_and_reset",
          email: email.trim().toLowerCase(),
          otp,
          newPassword,
        },
      });

      if (error) throw error;
      toast({ title: t('forgotPassword.passwordChanged'), description: t('forgotPassword.passwordChangedDesc') });
      handleOpenChange(false);
    } catch (err: any) {
      toast({ title: t('auth.error'), description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const stepTitles: Record<Step, string> = {
    email: t('forgotPassword.title'),
    otp: t('forgotPassword.otpTitle'),
    newPassword: t('forgotPassword.newPasswordTitle'),
  };

  const stepDescs: Record<Step, string> = {
    email: t('forgotPassword.emailDesc'),
    otp: t('forgotPassword.otpDesc'),
    newPassword: t('forgotPassword.newPasswordDesc'),
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors">
          {t('auth.forgotPassword')}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{stepTitles[step]}</DialogTitle>
          <DialogDescription>{stepDescs[step]}</DialogDescription>
        </DialogHeader>

        {step === "email" && (
          <form onSubmit={handleSendOtp} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="reset-email" type="email" placeholder="your@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pr-10 text-left" dir="ltr" />
              </div>
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={loading}>{loading ? t('forgotPassword.sending') : t('forgotPassword.sendCode')}</Button>
            </div>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleVerifyOtp} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {t('forgotPassword.otpSentTo')} <span className="font-medium text-foreground" dir="ltr">{email}</span>
            </p>
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} autoComplete="one-time-code">
                <InputOTPGroup dir="ltr">
                  <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                  <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {errors.otp && <p className="text-sm text-destructive text-center">{errors.otp}</p>}
            <div className="flex gap-2 justify-between pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setStep("email"); setOtp(""); }} className="gap-1">
                <ArrowLeft className="w-4 h-4" />{t('common.back')}
              </Button>
              <Button type="submit" disabled={otp.length !== 6 || loading}>
                {loading ? t('forgotPassword.verifying') : t('forgotPassword.verify')}
              </Button>
            </div>
          </form>
        )}

        {step === "newPassword" && (
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-pass">{t('changePassword.new')}</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="new-pass" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pr-10" />
              </div>
              {errors.newPassword && <p className="text-sm text-destructive">{errors.newPassword}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pass">{t('changePassword.confirm')}</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="confirm-pass" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pr-10" />
              </div>
              {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              <KeyRound className="w-4 h-4 ml-2" />
              {loading ? t('changePassword.saving') : t('forgotPassword.resetButton')}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
